import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Grid, Row, Col, Button, FormControl, Overlay, Popover, ListGroup, ListGroupItem, Panel } from 'react-bootstrap';
import dayjs from '../util/date.js';
import { trackEvent } from '../util/Analytics.jsx';

import { useStateValue } from '../context/state.jsx';
import { setProjectActivities } from '../context/datasets.js';
import { openAlert } from '../context/alert.js';
import { isProjectFilterActive, setProjectFilterSearchText } from '../context/projectFilter.js';
import { setProgress } from '../offline-features/setProgress.js';
import Utils from '../util/Utils.jsx';

const MainTopNav = ({
	setIsFlyoutLeftActive,
	setIsFlyoutRightActive,
	isFlyoutLeftActive,
	isFlyoutRightActive,
	viewsButtonRef,
	projectsButtonRef,
	datasetId,
	filteredActivities,
	filteredActivitiesMap,
	bulkActivities
}) => {
	const [showActionChooser, setShowActionChooser] = useState(false);
	const [{
		datasets,
		projectActivities,
		projectActivitiesMap,
		projectFilter,
		projectViewType
	}, dispatch] = useStateValue();
	const actionsButton = useRef(null);
	const filterActive = isProjectFilterActive(projectFilter);

	const updateProgress = newActivity => {
		const progressData = {
			activityId: newActivity.activityId,
			name: newActivity.name,
			statusCode: newActivity.statusCode,
			start: newActivity.start,
			finish: newActivity.finish,
			actualStart: newActivity.actualStart,
			actualFinish: newActivity.actualFinish,
			targetStart: newActivity.targetStart,
			targetFinish: newActivity.targetFinish,
			earlyFinish: newActivity.earlyFinish,
			expectFinish: newActivity.expectFinish,
			remainingDuration: newActivity.remainingDuration,
			targetDuration: newActivity.targetDuration,
			percentComplete: newActivity.percentComplete,
			taskType: newActivity.taskType
		};

		const allowOffline = datasets[datasetId].AllowOffline;
		setProgress(dispatch, datasetId, allowOffline, progressData);
	};

	const handleStartChange = startType => {
		const activityStatus = startType === 'Not Started' ? 'active and completed' : 'unstarted';
		dispatch(openAlert(
			'Start actvities?',
			'Are you sure you want set the start date for the selected activities to: ' + startType + '? Only ' + activityStatus + ' non TT_FinMile activities will be changed. Also note, this action may hide the selected activites, depending on your current filter.',
			[{
				text: 'Cancel',
				action: () => true
			}, {
				text: 'Continue',
				action: () => {
					for (const activityId of bulkActivities) {
						const activity = filteredActivities[filteredActivitiesMap[activityId]];
						let newActivity;

						if (activity.taskType === 'TT_FinMile') continue;
						if (startType === 'As Planned' || startType === 'Today') {
							if (activity.statusCode !== 'TK_NotStart') continue;
							const startTS = startType === 'Today' ? dayjs().startOf('day').valueOf() : activity.targetStart,
								delta = dayjs(startTS).diff(activity.targetStart, 'days'),
								finishClone = dayjs(activity.targetFinish).add(delta, 'days');

							newActivity = {
								...activity,
								start: startTS,
								finish: finishClone.valueOf(),
								targetStart: startTS,
								targetFinish: finishClone.valueOf(),
								actualStart: startTS,
								statusCode: activity.taskType === 'TT_Mile' ? 'TK_Complete' : 'TK_Active',
								percentComplete: activity.taskType === 'TT_Mile' ? '100.0' : activity.percentComplete
							};
						} else {
							if (activity.statusCode === 'TK_NotStart') continue;
							newActivity = {
								...activity,
								statusCode: 'TK_NotStart',
								start: activity.targetStart,
								finish: activity.targetFinish,
								actualStart: 0,
								actualFinish: 0,
								remainingDuration: activity.targetDuration,
								percentComplete: '0.0'
							};
						}

						updateProgress(newActivity);
					}
				}
			}]
		));
	};

	const handleFinishChange = finishType => {
		dispatch(openAlert(
			'Finish activities?',
			'Are you sure you want set the completion date for the selected activities to: ' + finishType + '? Only unstarted and active non TT_Mile activities will be changed. Also note, this action may hide the selected activities, depending on your current filter.',
			[{
				text: 'Cancel',
				action: () => true
			}, {
				text: 'Continue',
				action: () => {
					for (const activityId of bulkActivities) {
						const activity = filteredActivities[filteredActivitiesMap[activityId]];
						let newActivity;

						if (activity.statusCode === 'TK_Complete' || activity.taskType === 'TT_Mile') continue;
						const finishTS = finishType === 'Today' ? dayjs().startOf('day').valueOf() : activity.targetFinish;

						if (activity.statusCode === 'TK_NotStart') {
							const delta = dayjs(finishTS).diff(activity.targetFinish, 'days'),
								startClone = dayjs(activity.targetStart).add(delta, 'days');

							newActivity = {
								...activity,
								start: startClone.valueOf(),
								finish: finishTS,
								actualStart: startClone.valueOf(),
								actualFinish: finishTS,
								expectFinish: 0,
								statusCode: 'TK_Complete',
								remainingDuration: '0.0',
								percentComplete: '100.0'
							};
						} else {
							newActivity = {
								...activity,
								finish: finishTS,
								actualFinish: finishTS,
								expectFinish: 0,
								statusCode: 'TK_Complete',
								remainingDuration: '0.0',
								percentComplete: '100.0'
							};
						}

						updateProgress(newActivity);
					}
				}
			}]
		));
	};

	const setActivityFlag = flag => {
		trackEvent('activity', 'click', 'set activity flag');

		const projectActivitiesTemp = [...projectActivities];

		Utils.ajax(
			'setActivityFlag',
			{
				dataset: datasetId,
				activities: bulkActivities,
				flag
			},
			dispatch,
			() => {
				for (const activityId of bulkActivities) {
					const activity = projectActivitiesTemp[projectActivitiesMap[activityId]];
					activity.flag = flag === '0' ? null : flag;
					activity.hasChanged = true;
				}
				dispatch(setProjectActivities(projectActivitiesTemp));
			}
		);
	};

	const revertActivity = () => {
		trackEvent('activity', 'click', 'revert activity');

		const changedIds = (bulkActivities || []).filter(activityId => {
			const activity = projectActivitiesMap && activityId in projectActivitiesMap
				? projectActivities[projectActivitiesMap[activityId]]
				: null;
			return Boolean(activity && activity.hasChanged);
		});

		if (!changedIds.length) {
			dispatch(openAlert(
				'Nothing to revert',
				'None of the selected activities have SiteWalker changes since import.'
			));
			return;
		}

		const projectActivitiesTemp = [...projectActivities];
		const skipped = (bulkActivities || []).length - changedIds.length;

		Utils.ajax(
			'revertActivity',
			{
				dataset: datasetId,
				activities: changedIds.join(',')
			},
			dispatch,
			() => {
				Utils.ajax(
					'getActivityInfo',
					{
						dataset: datasetId,
						activities: changedIds.join(',')
					},
					dispatch,
					result => {
						for (const activity of result.activities) {
							projectActivitiesTemp[projectActivitiesMap[activity.activityId]] = activity;
						}
						dispatch(setProjectActivities(projectActivitiesTemp));
						const skipNote = skipped > 0
							? ` Skipped ${skipped} unchanged.`
							: '';
						dispatch(openAlert(
							'Reversion',
							`Reverted ${changedIds.length} activit${changedIds.length === 1 ? 'y' : 'ies'} to the most recently imported state.${skipNote}`
						));
					}
				);
			}
		);
	};

	const datasetInfo = datasetId && datasets && datasetId in datasets ? datasets[datasetId] : null;
	let flagItems = null,
		startItems = null,
		finishItems = null;

	if (datasetInfo) {
		flagItems = datasetInfo.filterOptions.flags.map(flag => {
			return (
				<ListGroupItem key={flag.Code} className={`flag-${flag.Code}`} onClick={() => {
					setShowActionChooser(false);
					if (!bulkActivities.length) return;
					setActivityFlag(flag.Code);
				}}>
					{flag.Color} <span className="glyphicon glyphicon-flag"></span>
				</ListGroupItem>
			);
		});

		startItems = ['As Planned', 'Today', 'Not Started'].map((item, i) => {
			return (
				<ListGroupItem key={i} onClick={(e) => {
					setShowActionChooser(false);
					if (!bulkActivities.length) return;
					handleStartChange(item);
				}}>
					{item}
				</ListGroupItem>
			);
		});

		finishItems = ['As Planned', 'Today'].map((item, i) => {
			return (
				<ListGroupItem key={i} onClick={() => {
					setShowActionChooser(false);
					if (!bulkActivities.length) return;
					handleFinishChange(item);
				}}>
					{item}
				</ListGroupItem>
			);
		});
	}

	return (
		<div className="topNav">
			<Grid>
				<Row>
					<Col className="topNavLeftColumn" xs={4} md={4}>
						<Button
							ref={projectsButtonRef}
							className="projects"
							aria-expanded={!!isFlyoutLeftActive}
							aria-controls="projects-flyout"
							onClick={() => { setIsFlyoutLeftActive(true); }}>
							<span className="glyphicon glyphicon-cog leftGlyph"></span>
							Projects
						</Button>
					</Col>
					<Col className="topNavCenterColumn" xs={4} md={4}>
						<span className={classnames('project-name', { activeFilter: filterActive })}>
							{datasets && datasetId && datasetId in datasets ? datasets[datasetId].Name : null}
						</span>
						<Button
							ref={actionsButton}
							className="bulk-actions-button"
							onClick={() => { setShowActionChooser(true); }}>
							<span className="glyphicon glyphicon-ok leftGlyph"></span>
							Actions ({bulkActivities.length})
						</Button>
						<Overlay
							target={actionsButton.current}
							show={showActionChooser}
							placement="bottom"
							rootClose={true}
							onHide={() => { setShowActionChooser(false); }}>
							<Popover id="accessibility" className="bulk-actions-popover" title="Actions">
								<Panel>
									<Panel.Heading>
										<Panel.Title toggle>Flags</Panel.Title>
									</Panel.Heading>
									<Panel.Collapse>
										<ListGroup>{flagItems}</ListGroup>
									</Panel.Collapse>
								</Panel>
								<Panel>
									<Panel.Heading>
										<Panel.Title toggle>Start</Panel.Title>
									</Panel.Heading>
									<Panel.Collapse>
										<ListGroup>{startItems}</ListGroup>
									</Panel.Collapse>
								</Panel>
								<Panel>
									<Panel.Heading>
										<Panel.Title toggle>Finish</Panel.Title>
									</Panel.Heading>
									<Panel.Collapse>
										<ListGroup>{finishItems}</ListGroup>
									</Panel.Collapse>
								</Panel>
								<Button
									bsStyle="danger"
									onClick={() => {
										setShowActionChooser(false);
										if (!bulkActivities.length) return;
										dispatch(openAlert(
											'Revert to import?',
											'This will undo any changes (including added pictures) since the most recent import. Also note, this action may hide the selected activities, depending on your current filter. Continue?',
											[{
												text: 'No, cancel',
												action: () => true
											}, {
												bsStyle: 'danger',
												text: 'Revert to import',
												action: revertActivity
											}]
										));
									}}>
									Revert <span className="glyphicon glyphicon-trash"></span>
								</Button>
							</Popover>
						</Overlay>
					</Col>
					<Col className="topNavRightColumn" xs={4} md={4}>
						{datasetId
							? (
								<FormControl
									className="topNavQuickSearch"
									type="text"
									value={projectFilter.searchText || ''}
									placeholder="Search ID or name"
									onChange={e => {
										dispatch(setProjectFilterSearchText(e.target.value));
									}} />
							)
							: null}
						{projectViewType === 'Calendar' || projectViewType === 'Resource Graphs'
							? (
								<Button
									className="printView sm-icon-only"
									onClick={() => { window.print(); }}>
									<span className="glyphicon glyphicon-print leftGlyph"></span>
									<span>Print</span>
								</Button>
							)
							: null}
						<Button
							ref={viewsButtonRef}
							className="views sm-icon-only"
							aria-expanded={!!isFlyoutRightActive}
							aria-controls="views-flyout"
							onClick={() => { setIsFlyoutRightActive(true); }}>
							<span className="glyphicon glyphicon-th-list leftGlyph"></span>
							<span>Views</span>
						</Button>
					</Col>
				</Row>
			</Grid>
		</div>
	);
};

MainTopNav.propTypes = {
	setIsFlyoutLeftActive: PropTypes.func,
	setIsFlyoutRightActive: PropTypes.func,
	isFlyoutLeftActive: PropTypes.bool,
	isFlyoutRightActive: PropTypes.bool,
	viewsButtonRef: PropTypes.oneOfType([
		PropTypes.func,
		PropTypes.shape({ current: PropTypes.any })
	]),
	projectsButtonRef: PropTypes.oneOfType([
		PropTypes.func,
		PropTypes.shape({ current: PropTypes.any })
	]),
	datasetId: PropTypes.string,
	filteredActivities: PropTypes.array,
	filteredActivitiesMap: PropTypes.object,
	bulkActivities: PropTypes.array
};

export default MainTopNav;